import { supabase } from '../lib/supabase';

class AlunoPanelService {
  // Função auxiliar para normalizar respostas
  normalizarResposta(resposta) {
    if (!resposta) return '';
    return resposta.toString()
      .trim()
      .replace(/\s+/g, ' ') // Múltiplos espaços -> espaço único
      .replace(/[""'']/g, '"') // Normalizar aspas
      .replace(/[–—]/g, '-') // Normalizar traços
      .toLowerCase();
  }

  // Função para verificar se resposta está correta
  verificarRespostaCorreta(respostaAluno, respostaCorreta, tipoQuestao, alternativas = null) {
    if (!respostaAluno || respostaAluno === '') return false;
    if (!respostaCorreta && respostaCorreta !== 0) return false;

    switch (tipoQuestao) {
      case 'dissertativa':
        return respostaAluno.toString().trim().length > 0;
        
      case 'multipla_escolha':
        // Para múltipla escolha, resposta_correta é um índice
        if (alternativas && Array.isArray(alternativas)) {
          const indiceCorreto = parseInt(respostaCorreta);
          const textoCorreto = alternativas[indiceCorreto];
          
          if (textoCorreto) {
            const respostaNorm = this.normalizarResposta(respostaAluno);
            const gabaritoNorm = this.normalizarResposta(textoCorreto);
            return respostaNorm === gabaritoNorm;
          }
        }
        // Fallback: comparação direta se não há alternativas
        const respostaNorm = this.normalizarResposta(respostaAluno);
        const gabaritoNorm = this.normalizarResposta(respostaCorreta);
        return respostaNorm === gabaritoNorm;
        
      case 'verdadeiro_falso':
        const respNorm = this.normalizarResposta(respostaAluno);
        const gabNorm = this.normalizarResposta(respostaCorreta);
        
        const verdadeiroVariacoes = ['verdadeiro', 'true', 'v', 'sim', 's', '1'];
        const falsoVariacoes = ['falso', 'false', 'f', 'não', 'nao', 'n', '0'];
        
        const respostaEhVerdadeiro = verdadeiroVariacoes.includes(respNorm);
        const respostaEhFalso = falsoVariacoes.includes(respNorm);
        const gabaritoEhVerdadeiro = verdadeiroVariacoes.includes(gabNorm);
        const gabaritoEhFalso = falsoVariacoes.includes(gabNorm);
        
        return (respostaEhVerdadeiro && gabaritoEhVerdadeiro) || (respostaEhFalso && gabaritoEhFalso);
        
      default:
        return false;
    }
  }
  // Buscar dados do aluno logado - VERSÃO OTIMIZADA
  async getAlunoData(userEmail = null) {
    console.log('🚀 Buscando dados do aluno...');
    
    try {
      let emailToSearch = userEmail;
      
      // Se não foi fornecido email, tentar buscar primeiro aluno disponível
      if (!emailToSearch) {
        console.log('🔄 Email não fornecido, buscando primeiro aluno...');
        const { data: fallback, error: fallbackError } = await supabase
          .from('alunos')
          .select(`
            *,
            escolas:escola_id(id, nome),
            anos_escolares:ano_escolar_id(id, nome)
          `)
          .limit(1)
          .single();
          
        if (fallbackError || !fallback) {
          throw new Error('Nenhum aluno encontrado no banco');
        }
        
        console.log('✅ Usando aluno fallback:', fallback.nome);
        return fallback;
      }

      // Buscar aluno pelo email fornecido
      const { data: aluno, error } = await supabase
        .from('alunos')
        .select(`
          *,
          escolas:escola_id(id, nome),
          anos_escolares:ano_escolar_id(id, nome)
        `)
        .eq('email', emailToSearch)
        .single();

      if (error) {
        console.error('❌ Erro busca por email:', error);
        
        // Fallback: buscar primeiro aluno disponível
        console.log('🔄 Tentando buscar primeiro aluno...');
        const { data: fallback, error: fallbackError } = await supabase
          .from('alunos')
          .select(`
            *,
            escolas:escola_id(id, nome),
            anos_escolares:ano_escolar_id(id, nome)
          `)
          .limit(1)
          .single();
          
        if (fallbackError || !fallback) {
          throw new Error('Nenhum aluno encontrado no banco');
        }
        
        console.log('✅ Usando aluno fallback:', fallback.nome);
        return fallback;
      }

      console.log('✅ Aluno encontrado:', aluno?.nome);
      return aluno;
    } catch (error) {
      console.error('❌ Erro geral:', error);
      return null;
    }
  }

  // Buscar testes disponíveis para o aluno
  async getTestesDisponiveis(alunoId, anoEscolar, escolaId) {
    console.log('📚 Buscando testes disponíveis para aluno:', alunoId);
    
    try {
      // Buscar testes onde o aluno é participante
      const { data: testesParticipacao, error: participacaoError } = await supabase
        .from('teste_participantes')
        .select(`
          teste_id,
          ativo,
          data_adicionado
        `)
        .eq('aluno_id', alunoId)
        .eq('ativo', true);

      if (participacaoError) {
        console.error('❌ Erro ao buscar participação:', participacaoError);
        return [];
      }

      if (!testesParticipacao || testesParticipacao.length === 0) {
        console.log('ℹ️ Aluno não é participante de nenhum teste');
        return [];
      }

      const testeIds = testesParticipacao.map(tp => tp.teste_id);
      console.log('🎯 IDs dos testes de participação:', testeIds);

      // Buscar detalhes dos testes com JOIN direto
      const { data: testes, error: testesError } = await supabase
        .from('testes')
        .select(`
          id,
          nome,
          descricao,
          ativo,
          created_at,
          updated_at,
          materia_id,
          ano_escolar_id,
          escola_id,
          materias!inner(id, nome),
          anos_escolares!inner(id, nome),
          escolas!inner(id, nome)
        `)
        .in('id', testeIds)
        .eq('ativo', true)
        .order('created_at', { ascending: false });

      if (testesError) {
        console.error('❌ Erro ao buscar detalhes dos testes:', testesError);
        return [];
      }

      console.log('🔍 Testes encontrados:', testes);

      // Buscar execuções existentes do aluno
      const { data: execucoes, error: execucoesError } = await supabase
        .from('teste_execucoes')
        .select(`
          id,
          teste_id,
          aluno_id,
          tentativa_numero,
          status_execucao,
          pontuacao_obtida,
          percentual_acerto,
          status_aprovacao,
          data_inicio,
          data_fim,
          duracao_minutos,
          questoes_total,
          questoes_corretas
        `)
        .eq('aluno_id', alunoId)
        .in('teste_id', testeIds);

      if (execucoesError) {
        console.warn('⚠️ Erro ao buscar execuções:', execucoesError);
      }

      console.log('🔍 Execuções encontradas:', execucoes);

      // Combinar dados dos testes com execuções
      const testesCompletos = await Promise.all(testes.map(async (teste) => {
        const execucoesTeste = execucoes?.filter(ex => ex.teste_id === teste.id) || [];
        const ultimaExecucao = execucoesTeste.length > 0 
          ? execucoesTeste.reduce((prev, current) => 
              (prev.tentativa_numero > current.tentativa_numero) ? prev : current
            )
          : null;

        // Determinar status e ações disponíveis
        let status = 'disponivel';
        let podeIniciar = true;
        let tentativas = execucoesTeste.length;
        let tentativasMaximas = 3; // Configurável

        if (ultimaExecucao) {
          if (ultimaExecucao.status_execucao === 'em_andamento') {
            status = 'em_andamento';
            podeIniciar = true; // Pode continuar
          } else if (ultimaExecucao.status_execucao === 'finalizado') {
            status = 'finalizado';
            podeIniciar = false;
          } else if (ultimaExecucao.status_execucao === 'abandonado') {
            status = 'abandonado';
            podeIniciar = tentativas < tentativasMaximas;
          } else if (ultimaExecucao.status_execucao === 'tempo_esgotado') {
            status = 'tempo_esgotado';
            podeIniciar = tentativas < tentativasMaximas;
          }
        }

        // Buscar quantidade real de questões do teste
        const { data: questoesData, error: questoesError } = await supabase
          .from('teste_questoes')
          .select('id')
          .eq('teste_id', teste.id)
          .eq('ativo', true);

        const quantidadeQuestoes = questoesData?.length || 0;

        if (questoesError) {
          console.warn(`⚠️ Erro ao buscar questões do teste ${teste.id}:`, questoesError);
        }

        return {
          id: teste.id,
          nome: teste.nome,
          descricao: teste.descricao,
          ativo: teste.ativo,
          created_at: teste.created_at,
          updated_at: teste.updated_at,
          materia_id: teste.materia_id,
          ano_escolar_id: teste.ano_escolar_id,
          escola_id: teste.escola_id,
          materias: teste.materias,
          anos_escolares: teste.anos_escolares,
          escolas: teste.escolas,
          // Campos calculados
          status,
          podeIniciar,
          tentativas,
          tentativasMaximas,
          ultimaExecucao,
          execucoes: execucoesTeste,
          // Campos com valores reais do banco
          duracao_minutos: teste.duracao_minutos || 60,
          questoes: quantidadeQuestoes,
          pontuacao_maxima: teste.pontuacao_maxima || 100
        };
      }));

      console.log(`✅ ${testesCompletos.length} testes encontrados com dados reais`);
      return testesCompletos;
    } catch (error) {
      console.error('❌ Erro geral ao buscar testes:', error);
      return [];
    }
  }

  // Verificar se aluno pode participar do teste
  async verificarParticipacao(alunoId, testeId) {
    console.log('🔍 Verificando participação...');
    
    try {
      const { data: participacao, error } = await supabase
        .from('teste_participacoes')
        .select('*')
        .eq('aluno_id', alunoId)
        .eq('teste_id', testeId)
        .single();

      // Se não encontrou participação, aluno pode participar
      const podeParticipar = error && error.code === 'PGRST116';
      console.log('✅ Pode participar:', podeParticipar);
      return podeParticipar;
    } catch (error) {
      console.log('✅ Aluno pode participar (sem participação anterior)');
      return true;
    }
  }

  // Buscar questões do teste
  async getQuestoesTeste(testeId) {
    console.log('❓ Buscando questões do teste...');
    
    try {
      const { data: questoes, error } = await supabase
        .from('teste_questoes')
        .select('*')
        .eq('teste_id', testeId)
        .eq('ativo', true)
        .order('ordem');

      if (error) {
        console.error('❌ Erro ao buscar questões:', error);
        return [];
      }

      console.log(`✅ ${questoes?.length || 0} questões encontradas`);
      return questoes || [];
    } catch (error) {
      console.error('❌ Erro geral questões:', error);
      return [];
    }
  }

  // Buscar resultados históricos do aluno
  async getResultadosHistoricos(alunoId) {
    console.log('📊 Buscando resultados históricos...');
    
    try {
      const { data: resultados, error } = await supabase
        .from('teste_execucoes')
        .select(`
          *,
          testes:teste_id(
            nome, 
            materias:materia_id(nome),
            pontuacao_maxima
          )
        `)
        .eq('aluno_id', alunoId)
        .eq('status_execucao', 'finalizado')
        .not('pontuacao_obtida', 'is', null)
        .order('data_fim', { ascending: false });

      if (error) {
        console.error('❌ Erro ao buscar resultados:', error);
        return [];
      }

      // Formatar resultados para compatibilidade com a interface
      const resultadosFormatados = resultados.map(resultado => ({
        id: resultado.id,
        nomeTest: resultado.testes?.nome || 'Teste não encontrado',
        materia: resultado.testes?.materias?.nome || 'Matéria não informada',
        pontuacao: resultado.pontuacao_obtida || 0,
        pontuacaoMaxima: resultado.testes?.pontuacao_maxima || 100,
        percentual: resultado.percentual_acerto || 0,
        status: resultado.status_aprovacao || 'pendente',
        dataRealizacao: resultado.data_fim || resultado.created_at,
        tempo: resultado.duracao_minutos || 0,
        questoesCorretas: Math.round((resultado.percentual_acerto / 100) * (resultado.questoes_total || 1)),
        questoesTotal: resultado.questoes_total || 1,
        tentativa: resultado.tentativa_numero || 1
      }));

      console.log(`✅ ${resultadosFormatados.length} resultados encontrados`);
      return resultadosFormatados;
    } catch (error) {
      console.error('❌ Erro geral resultados:', error);
      return [];
    }
  }

  // Buscar estatísticas do aluno
  async getEstatisticasAluno(alunoId) {
    console.log('📈 Calculando estatísticas...');
    
    try {
      const resultados = await this.getResultadosHistoricos(alunoId);
      
      if (!resultados || resultados.length === 0) {
        return {
          totalTestes: 0,
          mediaGeral: 0,
          aprovacoes: 0,
          reprovacoes: 0,
          melhorNota: 0,
          ultimoTeste: null
        };
      }

      const totalTestes = resultados.length;
      const somaNotas = resultados.reduce((sum, r) => sum + (r.pontuacao || 0), 0);
      const mediaGeral = somaNotas / totalTestes;
      const aprovacoes = resultados.filter(r => r.status === 'aprovado').length;
      const reprovacoes = resultados.filter(r => r.status === 'reprovado').length;
      const melhorNota = Math.max(...resultados.map(r => r.pontuacao || 0));
      const ultimoTeste = resultados[0]; // Já ordenado por data

      const stats = {
        totalTestes,
        mediaGeral: Math.round(mediaGeral * 100) / 100,
        aprovacoes,
        reprovacoes,
        melhorNota,
        ultimoTeste
      };

      console.log('✅ Estatísticas calculadas:', stats);
      return stats;
    } catch (error) {
      console.error('❌ Erro ao calcular estatísticas:', error);
      return {
        totalTestes: 0,
        mediaGeral: 0,
        aprovacoes: 0,
        reprovacoes: 0,
        melhorNota: 0,
        ultimoTeste: null
      };
    }
  }

  // Iniciar teste para o aluno
  async iniciarTeste(testeId, alunoId) {
    console.log('�� Iniciando teste:', { testeId, alunoId });
    
    // Validações
    if (!testeId) {
      console.error('❌ testeId é obrigatório');
      return { success: false, error: 'ID do teste é obrigatório' };
    }
    
    if (!alunoId) {
      console.error('❌ alunoId é obrigatório');
      return { success: false, error: 'ID do aluno é obrigatório' };
    }
    
    try {
      // Verificar se já existe uma execução em andamento
      const { data: execucaoExistente, error: checkError } = await supabase
        .from('teste_execucoes')
        .select('*')
        .eq('teste_id', testeId)
        .eq('aluno_id', alunoId)
        .eq('status_execucao', 'em_andamento')
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        console.error('❌ Erro ao verificar execução existente:', checkError);
        throw new Error('Erro ao verificar execução do teste');
      }

      let execucaoId;
      
      if (execucaoExistente) {
        // Continuar execução existente
        console.log('🔄 Continuando execução existente:', execucaoExistente.id);
        execucaoId = execucaoExistente.id;
      } else {
        // Criar nova execução
        const { data: novaExecucao, error: createError } = await supabase
          .from('teste_execucoes')
          .insert({
            teste_id: testeId,
            aluno_id: alunoId,
            tentativa_numero: 1,
            status_execucao: 'em_andamento',
            data_inicio: new Date().toISOString(),
            status_aprovacao: 'pendente'
          })
          .select('id')
          .single();

        if (createError) {
          console.error('❌ Erro ao criar execução:', createError);
          throw new Error('Erro ao iniciar teste');
        }

        execucaoId = novaExecucao.id;
        console.log('✅ Nova execução criada:', execucaoId);
      }

      return {
        success: true,
        execucaoId,
        message: execucaoExistente ? 'Execução continuada' : 'Teste iniciado com sucesso'
      };
    } catch (error) {
      console.error('❌ Erro ao iniciar teste:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Finalizar teste
  async finalizarTeste(execucaoId, respostas, tempoGasto) {
    console.log('🏁 Finalizando teste:', execucaoId);
    
    try {
      // Primeiro, buscar dados da execução para obter o teste_id
      const { data: execucaoAtual, error: execucaoError } = await supabase
        .from('teste_execucoes')
        .select('teste_id')
        .eq('id', execucaoId)
        .single();

      if (execucaoError || !execucaoAtual) {
        throw new Error('Execução não encontrada');
      }

      // Buscar questões do teste com respostas corretas e alternativas
      const { data: questoes, error: questoesError } = await supabase
        .from('teste_questoes')
        .select('id, resposta_correta, tipo_questao, alternativas')
        .eq('teste_id', execucaoAtual.teste_id)
        .eq('ativo', true);

      if (questoesError) {
        console.error('❌ Erro ao buscar questões:', questoesError);
        throw new Error('Erro ao buscar questões do teste');
      }

      // Calcular pontuação
      let questoesCorretas = 0;
      const totalQuestoes = questoes.length;

      questoes.forEach(questao => {
        const respostaAluno = respostas[questao.id];
        const respostaCorreta = questao.resposta_correta;

        console.log(`🔍 Verificando questão ${questao.id}:`, {
          tipo: questao.tipo_questao,
          respostaAluno,
          respostaCorreta,
          temResposta: !!respostaAluno,
          temGabarito: !!respostaCorreta
        });

        // Usar função auxiliar para verificar se está correta
        const acertou = this.verificarRespostaCorreta(respostaAluno, respostaCorreta, questao.tipo_questao, questao.alternativas);
        
        console.log(`📋 Detalhes da verificação:`, {
          questaoId: questao.id,
          tipo: questao.tipo_questao,
          respostaAluno,
          respostaCorreta,
          alternativas: questao.alternativas,
          textoCorreto: questao.alternativas && questao.tipo_questao === 'multipla_escolha' 
            ? questao.alternativas[parseInt(respostaCorreta)] 
            : 'N/A',
          resultado: acertou ? 'ACERTOU' : 'ERROU'
        });

        if (acertou) {
          questoesCorretas++;
          console.log(`✅ Questão ${questao.id}: ACERTOU`);
        } else {
          console.log(`❌ Questão ${questao.id}: ERROU`);
        }
      });

      // Calcular percentual de acerto
      const percentualAcerto = totalQuestoes > 0 ? Math.round((questoesCorretas / totalQuestoes) * 100) : 0;
      
      // Buscar pontuação máxima do teste
      const { data: teste, error: testeError } = await supabase
        .from('testes')
        .select('pontuacao_maxima, nota_minima_aprovacao')
        .eq('id', execucaoAtual.teste_id)
        .single();

      const pontuacaoMaxima = teste?.pontuacao_maxima || 100;
      const notaMinimaAprovacao = teste?.nota_minima_aprovacao || 60;
      
      // Calcular pontuação obtida
      const pontuacaoObtida = Math.round((questoesCorretas / totalQuestoes) * pontuacaoMaxima);
      
      // Determinar status de aprovação
      const statusAprovacao = percentualAcerto >= notaMinimaAprovacao ? 'aprovado' : 'reprovado';

      console.log('📊 Cálculos do teste:', {
        questoesCorretas,
        totalQuestoes,
        percentualAcerto,
        pontuacaoObtida,
        pontuacaoMaxima,
        statusAprovacao
      });

      // Atualizar execução com os resultados calculados
      const { data: execucao, error: updateError } = await supabase
        .from('teste_execucoes')
        .update({
          status_execucao: 'finalizado',
          data_fim: new Date().toISOString(),
          duracao_minutos: tempoGasto,
          respostas: respostas,
          questoes_total: totalQuestoes,
          questoes_corretas: questoesCorretas,
          pontuacao_obtida: pontuacaoObtida,
          percentual_acerto: percentualAcerto,
          status_aprovacao: statusAprovacao
        })
        .eq('id', execucaoId)
        .select('*')
        .single();

      if (updateError) {
        console.error('❌ Erro ao finalizar teste:', updateError);
        throw new Error('Erro ao finalizar teste');
      }

      console.log('✅ Teste finalizado com cálculos:', execucaoId);
      return {
        success: true,
        execucao,
        resultados: {
          questoesCorretas,
          totalQuestoes,
          percentualAcerto,
          pontuacaoObtida,
          statusAprovacao
        },
        message: 'Teste finalizado com sucesso'
      };
    } catch (error) {
      console.error('❌ Erro ao finalizar teste:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Verificar estrutura real das tabelas
  async verificarEstruturaTabelas() {
    console.log('🔍 Verificando estrutura das tabelas...');
    
    try {
      // Verificar tabela testes
      const { data: testes, error: testesError } = await supabase
        .from('testes')
        .select('*')
        .limit(5);

      if (testesError) {
        console.error('❌ Erro ao verificar tabela testes:', testesError);
      } else {
        console.log('📊 Tabela testes (primeiros 5 registros):', testes);
      }

      // Verificar tabela teste_participantes
      const { data: participantes, error: participantesError } = await supabase
        .from('teste_participantes')
        .select('*')
        .limit(5);

      if (participantesError) {
        console.error('❌ Erro ao verificar tabela teste_participantes:', participantesError);
      } else {
        console.log('👥 Tabela teste_participantes (primeiros 5 registros):', participantes);
      }

      // Verificar tabela teste_execucoes
      const { data: execucoes, error: execucoesError } = await supabase
        .from('teste_execucoes')
        .select('*')
        .limit(5);

      if (execucoesError) {
        console.error('❌ Erro ao verificar tabela teste_execucoes:', execucoesError);
      } else {
        console.log('🏃 Tabela teste_execucoes (primeiros 5 registros):', execucoes);
      }

      return {
        testes: testes || [],
        participantes: participantes || [],
        execucoes: execucoes || []
      };
    } catch (error) {
      console.error('❌ Erro ao verificar estrutura:', error);
      return { testes: [], participantes: [], execucoes: [] };
    }
  }

  // Buscar dados reais de teste (método simplificado)
  async getTestesReais(alunoId) {
    console.log('🎯 Buscando testes reais para aluno:', alunoId);
    
    try {
      // Primeiro, verificar se há dados nas tabelas
      const estrutura = await this.verificarEstruturaTabelas();
      
      if (estrutura.testes.length === 0) {
        console.log('ℹ️ Nenhum teste encontrado na tabela testes');
        return [];
      }

      if (estrutura.participantes.length === 0) {
        console.log('ℹ️ Nenhum participante encontrado na tabela teste_participantes');
        return [];
      }

      // Buscar testes onde o aluno é participante
      const { data: participacoes, error: participacaoError } = await supabase
        .from('teste_participantes')
        .select(`
          teste_id,
          ativo
        `)
        .eq('aluno_id', alunoId)
        .eq('ativo', true);

      if (participacaoError) {
        console.error('❌ Erro ao buscar participações:', participacaoError);
        return [];
      }

      if (participacoes.length === 0) {
        console.log('ℹ️ Aluno não é participante de nenhum teste');
        return [];
      }

      const testeIds = participacoes.map(p => p.teste_id);
      console.log('🎯 IDs dos testes de participação:', testeIds);

      // Buscar dados reais dos testes
      const { data: testes, error: testesError } = await supabase
        .from('testes')
        .select(`
          id,
          nome,
          descricao,
          ativo,
          created_at,
          updated_at,
          materia_id,
          ano_escolar_id,
          escola_id
        `)
        .in('id', testeIds)
        .eq('ativo', true);

      if (testesError) {
        console.error('❌ Erro ao buscar testes:', testesError);
        return [];
      }

      console.log('✅ Testes reais encontrados:', testes);

      // Buscar dados relacionados
      const testesCompletos = await Promise.all(
        testes.map(async (teste) => {
          // Buscar matéria
          let materia = null;
          if (teste.materia_id) {
            const { data: mat } = await supabase
              .from('materias')
              .select('id, nome')
              .eq('id', teste.materia_id)
              .single();
            materia = mat;
          }

          // Buscar ano escolar
          let anoEscolar = null;
          if (teste.ano_escolar_id) {
            const { data: ano } = await supabase
              .from('anos_escolares')
              .select('id, nome')
              .eq('id', teste.ano_escolar_id)
              .single();
            anoEscolar = ano;
          }

          // Buscar escola
          let escola = null;
          if (teste.escola_id) {
            const { data: esc } = await supabase
              .from('escolas')
              .select('id, nome')
              .eq('id', teste.escola_id)
              .single();
            escola = esc;
          }

          // Buscar execuções do aluno para este teste
          const { data: execucoes } = await supabase
            .from('teste_execucoes')
            .select('*')
            .eq('teste_id', teste.id)
            .eq('aluno_id', alunoId);

          // Determinar status baseado nas execuções
          let status = 'disponivel';
          let podeIniciar = true;
          let tentativas = execucoes?.length || 0;

          if (execucoes && execucoes.length > 0) {
            const ultimaExecucao = execucoes[execucoes.length - 1];
            
            if (ultimaExecucao.status_execucao === 'em_andamento') {
              status = 'em_andamento';
              podeIniciar = true;
            } else if (ultimaExecucao.status_execucao === 'finalizado') {
              status = 'finalizado';
              podeIniciar = false;
            } else if (ultimaExecucao.status_execucao === 'abandonado') {
              status = 'abandonado';
              podeIniciar = tentativas < 3;
            }
          }

          // Buscar quantidade real de questões
          const { data: questoesCount } = await supabase
            .from('teste_questoes')
            .select('id', { count: 'exact' })
            .eq('teste_id', teste.id)
            .eq('ativo', true);

          const quantidadeQuestoes = questoesCount?.length || 0;

          return {
            ...teste,
            materias: materia,
            anos_escolares: anoEscolar,
            escolas: escola,
            status,
            podeIniciar,
            tentativas,
            tentativasMaximas: 3,
            execucoes: execucoes || [],
            ultimaExecucao: execucoes && execucoes.length > 0 ? execucoes[execucoes.length - 1] : null,
            // Campos com valores reais do banco
            duracao_minutos: teste.duracao_minutos || 60,
            questoes: quantidadeQuestoes,
            pontuacao_maxima: teste.pontuacao_maxima || 100
          };
        })
      );

      console.log('✅ Testes completos com dados reais:', testesCompletos);
      return testesCompletos;
    } catch (error) {
      console.error('❌ Erro ao buscar testes reais:', error);
      return [];
    }
  }

  // Buscar materiais disponíveis para o ano escolar do aluno
  async getMateriais(anoEscolarId) {
    try {
      console.log('🔍 Buscando materiais para ano escolar:', anoEscolarId);
      
      const { data, error } = await supabase
        .from('conteudos')
        .select(`
          id,
          nome,
          arquivo_url,
          arquivo_nome,
          arquivo_tamanho,
          arquivo_tipo,
          downloads,
          created_at,
          materias (
            id,
            nome
          )
        `)
        .eq('ano_escolar_id', anoEscolarId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Erro ao buscar materiais:', error);
        throw error;
      }

      console.log('✅ Materiais encontrados:', data?.length || 0);
      return data || [];
    } catch (error) {
      console.error('❌ Erro no serviço de materiais:', error);
      return [];
    }
  }

  // Incrementar contador de download
  async incrementarDownload(conteudoId) {
    try {
      const { error } = await supabase
        .rpc('increment_download_count', { conteudo_id: conteudoId });

      if (error) {
        console.error('❌ Erro ao incrementar download:', error);
        throw error;
      }

      console.log('✅ Download incrementado para conteúdo:', conteudoId);
      return { success: true };
    } catch (error) {
      console.error('❌ Erro ao incrementar download:', error);
      return { success: false, error: error.message };
    }
  }

  // Atualizar senha do aluno
  async updatePassword(alunoId, newPassword) {
    console.log('🔐 Atualizando senha do aluno:', alunoId);
    
    try {
      // Primeiro buscar o email do aluno
      const { data: alunoData, error: alunoError } = await supabase
        .from('alunos')
        .select('id, nome, email')
        .eq('id', alunoId)
        .single();

      if (alunoError || !alunoData) {
        throw new Error('Aluno não encontrado');
      }

      // Atualizar senha no Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (authError) {
        console.error('❌ Erro ao atualizar senha no Auth:', authError);
        throw new Error('Erro ao atualizar senha de autenticação');
      }

      // Também atualizar na tabela alunos para manter sincronização
      const { error: tableError } = await supabase
        .from('alunos')
        .update({ 
          senha: newPassword,
          updated_at: new Date().toISOString()
        })
        .eq('id', alunoId);

      if (tableError) {
        console.warn('⚠️ Erro ao atualizar senha na tabela alunos:', tableError);
        // Não falhar aqui, pois o Auth já foi atualizado
      }

      console.log('✅ Senha atualizada com sucesso para:', alunoData.nome);
      return {
        success: true,
        message: 'Senha atualizada com sucesso',
        data: alunoData
      };
    } catch (error) {
      console.error('❌ Erro ao atualizar senha:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Buscar matérias disponíveis
  async getMaterias() {
    try {
      console.log('📚 Buscando matérias disponíveis...');
      
      const { data, error } = await supabase
        .from('materias')
        .select('id, nome, created_at')
        .order('nome', { ascending: true });

      if (error) {
        console.error('❌ Erro ao buscar matérias:', error);
        throw error;
      }

      console.log('✅ Matérias encontradas:', data?.length || 0);
      return data || [];
    } catch (error) {
      console.error('❌ Erro no serviço de matérias:', error);
      return [];
    }
  }
}

export default new AlunoPanelService();
